<template>
  <div 
    class="tw-px-3 tw-py-2.5 hover:tw-bg-gray-50 tw-transition-colors tw-cursor-pointer"
    :class="{ 'tw-bg-red-50 hover:tw-bg-red-100': notification.unread }"
    @click="handleNotificationClick"
  >
    <div class="tw-flex tw-items-center tw-gap-2">
      <!-- User Image / Icon -->
      <div class="tw-w-12 tw-h-12 tw-flex tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-red-100 tw-border tw-border-solid tw-border-red-200 tw-overflow-hidden">
        <img 
          v-if="userProfileImage && !imageError" 
          :src="userProfileImage" 
          :alt="userName"
          class="tw-w-full tw-h-full tw-object-cover"
          @error="handleImageError"
        />
        <icon v-else icon="solar:close-circle-bold" class="tw-text-red-600 tw-text-xl" />
      </div>
      
      <!-- Content -->
      <div class="tw-flex-1 tw-min-w-0">
        <div class="tw-flex tw-items-start tw-justify-between">
          <p class="tw-text-sm tw-font-medium tw-text-gray-800 tw-truncate">
            {{ notification.title }}
          </p>
          <span class="tw-text-xs tw-text-gray-400 tw-whitespace-nowrap tw-ml-1">{{ formatTime(notification.created_at || notification.time) }}</span>
        </div>
        <p class="tw-text-xs tw-text-gray-500 tw-mt-0.5 tw-line-clamp-1">{{ notification.message }}</p>
        
        <!-- Meeting details preview -->
        <div v-if="meetingData" class="tw-mt-1 tw-space-y-0.5">
          <div v-if="meetingData.title" class="tw-text-xs tw-font-medium tw-text-gray-700 tw-truncate">
            {{ meetingData.title }}
          </div>
          <div v-if="meetingData.declined_by" class="tw-text-xs tw-text-red-600 tw-hidden">
            <icon icon="solar:user-cross-bold-duotone" class="tw-inline tw-mr-1" />
            {{ $t('messages.declined-by', 'Declined by') }}: {{ meetingData.declined_by.name }}
          </div>
          <div v-if="meetingData.scheduled_at" class="tw-text-xs tw-text-gray-500">
            <icon icon="solar:calendar-bold-duotone" class="tw-inline tw-mr-1" />
            {{ formatMeetingDateTime(meetingData.scheduled_at) }}
          </div>
        </div>
      
        <!-- Action buttons -->
        <div v-if="meetingData && meetingData.actions && meetingData.actions.length" class="tw-w-full tw-grid tw-grid-cols-2 tw-mt-2 tw-gap-1.5">
          <a-button 
            v-for="action in meetingData.actions.slice(0, 2)" 
            :key="action.action"
            :intent="getButtonIntent(action.style)"
            full 
            class="!tw-min-h-[0px] tw-h-[25px]"
            :loading="isProcessing && actionType === action.action"
            :disabled="isProcessing"
            @click.stop="handleAction(action)"
          >
            <span class="tw-text-xs">{{ action.label }}</span>
          </a-button>
        </div>
      </div>
    </div>
  </div>

  <!-- Meeting Details Popup -->
  <MeetingDetailsPopup 
    :visible="showMeetingPopup"
    :notification="popupNotification"
    @close="closeMeetingPopup"
    @meetingAccepted="handleMeetingAccepted"
    @meetingDeclined="handleMeetingDeclined"
  />
</template>

<script setup>
import { defineProps, defineEmits, ref, computed } from 'vue';
import { useI18n } from "vue-i18n";
import meetingsApi from '@/api/meetings';
import useAlert from '@/composables/useAlert';
import MeetingDetailsPopup from '../modals/MeetingDetailsPopup.vue';

const { t } = useI18n();

// Define props
const props = defineProps({
  notification: {
    type: Object,
    required: true
  }
});

// Define emits
const emit = defineEmits(['notificationRead', 'popupStateChanged']);

// Reactive state management
const isProcessing = ref(false);
const actionType = ref(null);
const showMeetingPopup = ref(false);
const fullMeetingDetails = ref(null);

// Extract meeting data from notification
const meetingData = computed(() => {
  return props.notification?.data || null;
});

// Get user profile image (declined_by or canceled_by)
const userProfileImage = computed(() => {
  return meetingData.value?.declined_by?.profile_image || 
         meetingData.value?.canceled_by?.profile_image || 
         null;
});

// Get user name (declined_by or canceled_by)
const userName = computed(() => {
  return meetingData.value?.declined_by?.name || 
         meetingData.value?.canceled_by?.name || 
         'User';
});

// Handle image error - fallback to icon
const imageError = ref(false);
function handleImageError(event) {
  imageError.value = true;
  event.target.style.display = 'none';
}

// Handle notification click (opens popup)
async function handleNotificationClick(event) {
  // Don't open popup if clicking on buttons
  if (event.target.closest('button') || event.target.closest('.tw-grid')) {
    return;
  }
  
  await openMeetingPopup();
}

// Open meeting popup
async function openMeetingPopup() {
  const meetingId = meetingData.value?.meeting_id;
  if (!meetingId) {
    useAlert(t('errors.meeting-id-not-found', 'Meeting ID not found'), 'warning');
    return;
  }
  
  try {
    // Fetch full meeting details
    const response = await meetingsApi.get(meetingId);
    fullMeetingDetails.value = response.data?.data;
    
    // Open popup
    showMeetingPopup.value = true;
    emit('popupStateChanged', true);
    
    // Mark notification as read
    if (props.notification.unread) {
      emit('notificationRead', props.notification.id);
    }
  } catch (error) {
    console.error('Error fetching meeting details:', error);
    const errorMessage = error.response?.data?.message || error.message || t('errors.something-went-wrong', 'Something went wrong');
    useAlert(errorMessage, 'danger');
  }
}

// Close meeting popup
function closeMeetingPopup() {
  showMeetingPopup.value = false;
  emit('popupStateChanged', false);
}

// Handle action from action buttons
async function handleAction(action) {
  if (isProcessing.value) return;
  
  const meetingId = meetingData.value?.meeting_id;
  if (!meetingId) {
    useAlert(t('errors.meeting-id-not-found', 'Meeting ID not found'), 'warning');
    return;
  }
  
  try {
    isProcessing.value = true;
    actionType.value = action.action;
    
    switch (action.action) {
      case 'view_details':
        await openMeetingPopup();
        break;
        
      default:
        console.warn('Unknown action:', action.action);
    }
    
    // Mark notification as read
    if (props.notification.unread) {
      emit('notificationRead', props.notification.id);
    }
  } catch (error) {
    console.error('Error handling action:', error);
    const errorMessage = error.response?.data?.message || error.message || t('errors.something-went-wrong', 'Something went wrong');
    useAlert(errorMessage, 'danger');
  } finally {
    isProcessing.value = false;
    actionType.value = null;
  }
}

// Get button intent from action style
function getButtonIntent(style) {
  const styleMap = {
    'primary': 'gradient',
    'secondary': 'secondary',
    'danger': 'danger',
    'info': 'info',
    'success': 'success'
  };
  return styleMap[style] || 'secondary';
}

// Handle meeting accepted from popup
async function handleMeetingAccepted() {
  // Update local state if needed
  if (meetingData.value) {
    meetingData.value.status = 'accepted';
  }
  closeMeetingPopup();
}

// Handle meeting declined from popup
async function handleMeetingDeclined() {
  // Update local state if needed
  if (meetingData.value) {
    meetingData.value.status = 'declined';
  }
  closeMeetingPopup();
}

// Create adapted notification object for popup
const popupNotification = computed(() => {
  let meeting = fullMeetingDetails.value || meetingData.value;
  
  if (!meeting) return props.notification;
  
  // Transform participants to include name directly
  const transformedParticipants = meeting.participants?.map(participant => ({
    ...participant,
    name: participant.user?.name || participant.name || 'Unknown'
  })) || [];
  
  // Transform meeting data to match popup expectations
  const transformedMeeting = {
    ...meeting,
    start_time: meeting.start_time || meeting.scheduled_at,
    end_time: meeting.end_time || (meeting.scheduled_at && meeting.duration_minutes 
      ? new Date(new Date(meeting.scheduled_at).getTime() + meeting.duration_minutes * 60000).toISOString()
      : null),
    meeting_url: meeting.meeting_url || meeting.room_url || meeting.host_room_url,
    location: meeting.location || null,
    participants: transformedParticipants
  };
  
  return {
    ...props.notification,
    data: {
      ...props.notification.data,
      meeting: transformedMeeting
    }
  };
});

// Format meeting date and time for display
function formatMeetingDateTime(dateString) {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    let dateText;
    if (date.toDateString() === today.toDateString()) {
      dateText = t('common.today', 'Today');
    } else if (date.toDateString() === tomorrow.toDateString()) {
      dateText = t('common.tomorrow', 'Tomorrow');
    } else {
      const month = date.toLocaleString(undefined, { month: 'short' });
      const day = date.getDate();
      dateText = `${month} ${day}`;
    }
    
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes.toString().padStart(2, '0');
    const timeText = `${displayHours}:${displayMinutes} ${ampm}`;
    
    return `${dateText} at ${timeText}`;
  } catch (error) {
    console.error('Error formatting meeting date time:', error);
    return '';
  }
}

// Format timestamp to relative time
const formatTime = (timestamp) => {
  const now = new Date();
  let date;
  
  if (typeof timestamp === 'string') {
    date = new Date(timestamp);
  } else if (typeof timestamp === 'number') {
    date = new Date(timestamp * 1000);
  } else {
    date = timestamp?.created_at ? new Date(timestamp.created_at) : new Date();
  }
  
  if (isNaN(date.getTime())) {
    date = new Date();
  }
  
  const diff = now - date;
  const minutes = Math.floor(diff / 60000);
  
  if (minutes < 60) {
    return minutes <= 0 
      ? t('time.just-now', 'Just now')
      : t('time.min-ago', { count: minutes }, `${minutes}m`);
  }
  
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return t('time.hour-ago', { count: hours }, `${hours}h`);
  }
  
  const days = Math.floor(hours / 24);
  return t('time.day-ago', { count: days }, `${days}d`);
};
</script>

